import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Search, Heart, ShoppingBag, X, Menu, User, ChevronRight } from "lucide-react";
import { storeApi, formatPrice, productUrl } from "@/lib/storefront";
import { useCart } from "@/contexts/CartContext";
import { useWishlist } from "@/contexts/WishlistContext";
import { useCustomerAuth } from "@/contexts/CustomerAuthContext";
import useStoreRefresh from "@/hooks/useStoreRefresh";

const NAV_ITEMS = [
  { label: "Shop all", href: "/catalog" },
  { label: "New", href: "/catalog?sort=-created_at" },
  { label: "Guides", href: "/guides/precut-sizes" },
  { label: "Wholesale", href: "/wholesale", accent: true },
  { label: "About", href: "/pages/our-story" },
  { label: "Help", href: "/faq" },
];

export default function StoreHeader() {
  const nav = useNavigate();
  const location = useLocation();
  const { count: cartCount, setOpen: setCartOpen } = useCart();
  const { count: wishCount } = useWishlist();
  const { isAuthed } = useCustomerAuth();

  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [suggest, setSuggest] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [megaOpen, setMegaOpen] = useState(false);
  const inputRef = useRef(null);
  const suggestTimer = useRef(null);
  const megaCloseTimer = useRef(null);

  // Delayed close so the user can move the cursor from the trigger into the
  // floating mega-menu without it collapsing under them.
  const openMega = useCallback(() => {
    if (megaCloseTimer.current) {
      clearTimeout(megaCloseTimer.current);
      megaCloseTimer.current = null;
    }
    setMegaOpen(true);
  }, []);
  const scheduleCloseMega = useCallback(() => {
    if (megaCloseTimer.current) clearTimeout(megaCloseTimer.current);
    megaCloseTimer.current = setTimeout(() => setMegaOpen(false), 180);
  }, []);
  const closeMegaNow = useCallback(() => {
    if (megaCloseTimer.current) {
      clearTimeout(megaCloseTimer.current);
      megaCloseTimer.current = null;
    }
    setMegaOpen(false);
  }, []);

  // Mega-menu category list — refetch on focus so admin deletions propagate
  // the moment the storefront tab is re-activated.
  useStoreRefresh(() => {
    storeApi.get("/categories/tree").then((r) => setCategories(r.data || [])).catch(() => { /* noop */ });
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
    closeMegaNow();
  }, [location.pathname, closeMegaNow]);

  useEffect(() => () => {
    if (megaCloseTimer.current) clearTimeout(megaCloseTimer.current);
  }, []);

  // Allow other components (e.g. MobileBottomNav) to toggle search via a window event.
  useEffect(() => {
    const handler = () => setSearchOpen(true);
    window.addEventListener("store:open-search", handler);
    return () => window.removeEventListener("store:open-search", handler);
  }, []);

  const runSuggest = useCallback((q) => {
    clearTimeout(suggestTimer.current);
    if (!q || q.length < 2) { setSuggest(null); return; }
    suggestTimer.current = setTimeout(() => {
      storeApi.get("/search", { params: { q, limit: 6 } })
        .then((r) => setSuggest(r.data))
        .catch(() => setSuggest(null));
    }, 180);
  }, []);

  useEffect(() => { if (searchOpen) setTimeout(() => inputRef.current?.focus(), 50); }, [searchOpen]);

  const onSubmit = (e) => {
    e.preventDefault();
    if (query.trim()) {
      nav(`/search?q=${encodeURIComponent(query.trim())}`);
      setSearchOpen(false);
    }
  };

  return (
    <>
      {/* Top banner */}
      <div className="border-b border-[#E6D4CC] bg-[#F1E3DC] py-2 text-center text-xs tracking-[0.2em] text-[#6B5158]" data-testid="store-topbar">
        <span className="uppercase">Save 10% on your first order — join the list · Free US shipping over $75</span>
      </div>

      <header className="sticky top-0 z-40 border-b border-[#E6D4CC] bg-[#FAF6F1]/95 backdrop-blur-md" data-testid="store-header">
        <div className="mx-auto flex h-20 max-w-[1440px] items-center gap-3 px-4 sm:h-24 sm:px-6 md:h-28 md:gap-6 md:px-10">
          {/* Left section: mobile hamburger / desktop nav */}
          <div className="flex flex-1 items-center md:flex-1">
            <button onClick={() => setMobileOpen(true)} className="p-1 md:hidden" aria-label="Open menu" data-testid="mobile-menu-btn">
              <Menu size={22} />
            </button>

            {/* Left nav (desktop) — elegant tracking + animated underline */}
            <nav className="hidden items-center gap-5 md:flex lg:gap-7" data-testid="primary-nav">
              {/* Shop all — simple link */}
              <Link
                to="/catalog"
                onMouseEnter={closeMegaNow}
                className="group relative inline-block whitespace-nowrap py-2 font-display text-[13px] font-normal tracking-[0.02em] text-black transition-colors hover:text-[#9D5C6C] lg:text-[14px]"
                data-testid="nav-shop-all"
              >
                <span>Shop all</span>
                <span className="absolute -bottom-0.5 left-0 h-px w-full origin-left scale-x-0 bg-black transition-transform duration-500 group-hover:scale-x-100" />
              </Link>

              {/* Categories — premium mega-menu with parent columns + subcategories */}
              <div
                className="group relative"
                onMouseEnter={openMega}
                onMouseLeave={scheduleCloseMega}
              >
                <button
                  type="button"
                  onClick={() => setMegaOpen((v) => !v)}
                  aria-haspopup="true"
                  aria-expanded={megaOpen}
                  className="relative inline-block whitespace-nowrap py-2 font-display text-[13px] font-normal tracking-[0.02em] text-black transition-colors hover:text-[#9D5C6C] lg:text-[14px]"
                  data-testid="nav-categories"
                >
                  <span>Categories</span>
                  <span className={`absolute -bottom-0.5 left-0 h-px w-full origin-left bg-black transition-transform duration-500 ${megaOpen ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"}`} />
                </button>
              {megaOpen && categories.length > 0 && (
                <div
                  className="absolute left-0 top-full pt-3"
                  onMouseEnter={openMega}
                  onMouseLeave={scheduleCloseMega}
                >
                  <div
                    className="w-[min(560px,calc(100vw-3rem))] rounded-md border border-[#E6D4CC] bg-white p-6 shadow-[0_22px_60px_-20px_rgba(0,0,0,0.18)] xl:w-[980px] xl:p-8"
                    data-testid="mega-menu"
                  >
                  <div className="mb-5 flex items-center justify-between border-b border-[#E6D4CC] pb-3">
                    <span className="text-[10px] uppercase tracking-[0.3em] text-[#6B5158]">Browse by category</span>
                    <Link
                      to="/catalog"
                      onClick={() => setMegaOpen(false)}
                      className="text-xs font-medium text-[#9D5C6C] hover:underline"
                      data-testid="mega-shop-all"
                    >
                      Shop all →
                    </Link>
                  </div>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-6 xl:grid-cols-4 xl:gap-x-8">
                    {categories.slice(0, 8).map((c) => (
                      <div key={c.id} className="space-y-3">
                        <Link
                          to={`/c/${c.slug}`}
                          onClick={() => setMegaOpen(false)}
                          className="group/cat block"
                          data-testid={`mega-parent-${c.slug}`}
                        >
                          {c.image ? (
                            <div className="aspect-[4/3] overflow-hidden rounded-md bg-[#FAF6F1]">
                              <img
                                src={c.image}
                                alt=""
                                className="h-full w-full object-cover transition-transform duration-700 group-hover/cat:scale-[1.04]"
                              />
                            </div>
                          ) : (
                            <div className="aspect-[4/3] rounded-md bg-[#F1E3DC]" />
                          )}
                          <div className="mt-2 font-display text-[17px] leading-tight text-[#2B2127] transition-colors group-hover/cat:text-[#9D5C6C]">
                            {c.name}
                          </div>
                        </Link>
                        {c.children && c.children.length > 0 && (
                          <ul className="space-y-1.5 text-[13px] text-[#6B5158]">
                            {c.children.slice(0, 5).map((sub) => (
                              <li key={sub.id}>
                                <Link
                                  to={`/c/${sub.slug}`}
                                  onClick={() => setMegaOpen(false)}
                                  className="hover:text-[#9D5C6C] hover:underline"
                                  data-testid={`mega-child-${sub.slug}`}
                                >
                                  {sub.name}
                                </Link>
                              </li>
                            ))}
                            {c.children.length > 5 && (
                              <li>
                                <Link
                                  to={`/c/${c.slug}`}
                                  onClick={() => setMegaOpen(false)}
                                  className="text-[12px] font-medium text-[#9D5C6C] hover:underline"
                                >
                                  View all {c.children.length} →
                                </Link>
                              </li>
                            )}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                  {categories.length > 8 && (
                    <div className="mt-6 border-t border-[#E6D4CC] pt-4 text-center">
                      <Link
                        to="/catalog"
                        onClick={() => setMegaOpen(false)}
                        className="text-sm font-medium text-[#9D5C6C] hover:underline"
                        data-testid="mega-browse-all"
                      >
                        Browse all {categories.length} categories →
                      </Link>
                    </div>
                  )}
                  </div>
                </div>
              )}
            </div>

            {NAV_ITEMS.filter((n) => n.label !== "Shop all").map((n) => (
              <Link
                key={n.href}
                to={n.href}
                onMouseEnter={closeMegaNow}
                className="group relative inline-block whitespace-nowrap py-2 font-display text-[13px] font-normal tracking-[0.02em] text-black transition-colors hover:text-[#9D5C6C] lg:text-[14px]"
                data-testid={`nav-${n.label.toLowerCase()}`}
              >
                <span className="flex items-center gap-1.5">
                  {n.label}
                  {n.accent && <span className="h-1.5 w-1.5 rounded-full bg-[#6D2E4B]" aria-hidden="true" />}
                </span>
                <span className="absolute -bottom-0.5 left-0 h-px w-full origin-left scale-x-0 bg-black transition-transform duration-500 group-hover:scale-x-100" />
              </Link>
            ))}
            </nav>
          </div>

          {/* Center: Logo (in normal flow, not absolute) */}
          <Link to="/" className="flex flex-shrink-0 items-center justify-center" data-testid="store-logo">
            <div className="flex flex-col items-center font-display font-normal leading-none tracking-[0.04em] text-[#2B2127] text-[15px] sm:text-[17px] md:text-[19px] lg:text-[21px]">
              <span className="whitespace-nowrap">B<span className="font-display-soft">i</span>scayne <span className="font-display-soft italic">Quilt</span> Supply</span>
              <span className="mt-1 hidden text-[8px] uppercase tracking-[0.34em] text-[#9D5C6C] md:block">Sewing · Quilting · Notions</span>
            </div>
          </Link>

          {/* Right icons */}
          <div className="flex flex-1 items-center justify-end gap-2 sm:gap-3 md:gap-4">
            <button onClick={() => setSearchOpen(true)} aria-label="Search" className="p-1 text-[#2B2127] hover:text-[#9D5C6C]" data-testid="search-btn">
              <Search size={18} className="sm:hidden" />
              <Search size={20} className="hidden sm:block" />
            </button>
            <Link to={isAuthed ? "/account" : "/account/login"} aria-label={isAuthed ? "My account" : "Sign in"} className="hidden p-1 text-[#2B2127] hover:text-[#9D5C6C] md:inline-flex" data-testid="account-btn">
              <User size={20} />
            </Link>
            <Link to="/wishlist" aria-label="Wishlist" className="relative p-1 text-[#2B2127] hover:text-[#9D5C6C]" data-testid="wishlist-btn">
              <Heart size={18} className="sm:hidden" />
              <Heart size={20} className="hidden sm:block" />
              {wishCount > 0 && (
                <span className="absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#9D5C6C] px-1 text-[10px] font-medium text-white">{wishCount}</span>
              )}
            </Link>
            <button onClick={() => setCartOpen(true)} aria-label="Cart" className="relative p-1 text-[#2B2127] hover:text-[#9D5C6C]" data-testid="cart-btn">
              <ShoppingBag size={18} className="sm:hidden" />
              <ShoppingBag size={20} className="hidden sm:block" />
              {cartCount > 0 && (
                <span className="absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#2B2127] px-1 text-[10px] font-medium text-white">{cartCount}</span>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Search overlay */}
      {searchOpen && (
        <div className="fixed inset-0 z-50 bg-[#2B2127]/30 backdrop-blur-sm" onClick={() => setSearchOpen(false)}>
          <div className="border-b border-[#E6D4CC] bg-[#FAF6F1] p-6 md:p-10" onClick={(e) => e.stopPropagation()} data-testid="search-overlay">
            <div className="mx-auto flex max-w-3xl items-center gap-4">
              <Search size={22} className="text-[#6B5158]" />
              <form onSubmit={onSubmit} className="flex-1">
                <input
                  ref={inputRef}
                  type="text"
                  placeholder="Search for products, brands, categories…"
                  value={query}
                  onChange={(e) => { setQuery(e.target.value); runSuggest(e.target.value); }}
                  className="w-full bg-transparent font-display text-2xl text-[#2B2127] outline-none placeholder:text-[#6B5158]/60"
                  data-testid="search-input"
                />
              </form>
              <button onClick={() => setSearchOpen(false)} className="p-1" aria-label="Close"><X size={22} /></button>
            </div>

            {suggest && (
              <div className="mx-auto mt-6 grid max-w-3xl gap-6 md:grid-cols-2">
                {suggest.products?.length > 0 && (
                  <div>
                    <div className="mb-2 text-xs uppercase tracking-[0.2em] text-[#6B5158]">Products</div>
                    <ul className="space-y-2">
                      {suggest.products.map((p) => (
                        <li key={p.id}>
                          <Link to={productUrl(p)} className="flex items-center gap-3 rounded-md p-2 hover:bg-white" data-testid={`suggest-${p.id}`}>
                            {p.primary_image ? <img src={p.primary_image} alt="" className="h-10 w-10 rounded object-cover" /> : <div className="h-10 w-10 rounded bg-[#E6D4CC]" />}
                            <div className="min-w-0 flex-1">
                              <div className="truncate text-sm">{p.title}</div>
                              <div className="text-xs text-[#6B5158]">{formatPrice(p.price, p.currency)}</div>
                            </div>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="space-y-4">
                  {suggest.categories?.length > 0 && (
                    <div>
                      <div className="mb-2 text-xs uppercase tracking-[0.2em] text-[#6B5158]">Categories</div>
                      <div className="flex flex-wrap gap-2">
                        {suggest.categories.map((c) => (
                          <Link key={c.id} to={`/c/${c.slug}`} className="rounded-full border border-[#E6D4CC] bg-white px-3 py-1 text-sm hover:bg-[#F1E3DC]">{c.name}</Link>
                        ))}
                      </div>
                    </div>
                  )}
                  {suggest.brands?.length > 0 && (
                    <div>
                      <div className="mb-2 text-xs uppercase tracking-[0.2em] text-[#6B5158]">Brands</div>
                      <div className="flex flex-wrap gap-2">
                        {suggest.brands.map((b) => (
                          <Link key={b.id} to={`/brand/${b.slug}`} className="rounded-full border border-[#E6D4CC] bg-white px-3 py-1 text-sm hover:bg-[#F1E3DC]">{b.name}</Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Mobile menu drawer — 2 pane: main → category detail */}
      {mobileOpen && (
        <MobileDrawer
          onClose={() => setMobileOpen(false)}
          categories={categories.filter((c) => !c.parent_id)}
          isAuthed={isAuthed}
        />
      )}
    </>
  );
}


function MobileDrawer({ onClose, categories, isAuthed }) {
  const [activeCat, setActiveCat] = useState(categories[0] || null);

  return (
    <div className="fixed inset-0 z-50 bg-[#2B2127]/40" onClick={onClose} data-testid="mobile-menu-drawer">
      <div
        className="relative flex h-full w-full bg-[#FAF6F1]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Left column — main menu + categories list (always visible) */}
        <div className="flex w-1/2 flex-col overflow-y-auto px-5 py-5">
          <div className="mb-5 flex items-center justify-between">
            <div className="font-display text-2xl">Menu</div>
            <button onClick={onClose} aria-label="Close" data-testid="mobile-menu-close">
              <X size={22} />
            </button>
          </div>

          <nav className="space-y-1">
            {NAV_ITEMS.map((n) => (
              <Link
                key={n.href}
                to={n.href}
                onClick={onClose}
                className="flex items-center justify-between py-2.5 font-display text-[18px] font-medium text-[#2B2127] hover:text-[#9D5C6C]"
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <span>{n.label}</span>
                {n.accent && <span className="ml-2 h-1.5 w-1.5 rounded-full bg-[#6D2E4B]" />}
              </Link>
            ))}

            {categories.length > 0 && (
              <>
                <div className="mt-5 mb-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#6B5158]">Categories</div>
                {categories.map((c) => {
                  const active = activeCat?.id === c.id;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setActiveCat(c)}
                      className={`flex w-full items-center justify-between py-2.5 text-left font-display text-[18px] font-medium transition-colors ${active ? "text-[#9D5C6C]" : "text-[#2B2127] hover:text-[#9D5C6C]"}`}
                      data-testid={`mobile-cat-row-${c.slug}`}
                    >
                      <span>{c.name}</span>
                      <ChevronRight size={18} className={active ? "text-[#9D5C6C]" : "text-[#C7BFAB]"} />
                    </button>
                  );
                })}
              </>
            )}
          </nav>

          <div className="mt-auto pt-6">
            <Link
              to={isAuthed ? "/account" : "/account/login"}
              onClick={onClose}
              className="flex items-center gap-2 text-sm text-[#6B5158] hover:text-[#2B2127]"
            >
              <User size={16} /> {isAuthed ? "My account" : "Sign in"}
            </Link>
          </div>
        </div>

        {/* Right column — subcategories of activeCat */}
        <div className="flex w-1/2 flex-col overflow-y-auto border-l border-[#E6D4CC] bg-[#EDE7D7] px-5 py-5" data-testid="mobile-cat-detail">
          {activeCat ? (
            <>
              {activeCat.image && (
                <Link
                  to={`/c/${activeCat.slug}`}
                  onClick={onClose}
                  className="mb-3 block aspect-[16/10] overflow-hidden rounded-md bg-white"
                  data-testid={`mobile-cat-image-${activeCat.slug}`}
                >
                  <img src={activeCat.image} alt={activeCat.name} className="h-full w-full object-cover" />
                </Link>
              )}
              <Link
                to={`/c/${activeCat.slug}`}
                onClick={onClose}
                className="font-display text-[22px] leading-tight text-[#2B2127] hover:text-[#9D5C6C]"
                data-testid={`mobile-cat-parent-${activeCat.slug}`}
              >
                {activeCat.name}
              </Link>
              <div className="mt-3 border-t border-[#E6D4CC] pt-1" />
              {activeCat.children?.length > 0 && (
                <ul className="space-y-0">
                  {activeCat.children.map((sub) => (
                    <li key={sub.id}>
                      <Link
                        to={`/c/${sub.slug}`}
                        onClick={onClose}
                        className="block border-b border-[#E6D4CC] py-2.5 text-[14px] leading-snug text-[#2B2127] hover:text-[#9D5C6C]"
                        data-testid={`mobile-cat-sub-${sub.slug}`}
                      >
                        {sub.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : (
            <div className="text-xs text-[#6B5158]/60">Tap a category</div>
          )}
        </div>
      </div>
    </div>
  );
}
